import { validateVfxRecipe, type MagicRecipe } from '../../shared/vfx-recipe-contract';
import { castCommitId, isCastItemRoll, isCastEffectRow } from './cast-commit';
import { itemField } from '../ability/context';
import { layerOf } from '../../shared/array-types';
import { SHAPES } from '../ability/aoe/shape-ids';
import { localAttackRolls } from './attack-roll-rows';
import { inventoryDefinitionVfxRecipe } from '../compile/from-base-inventory-assets';

export type RollVfxTypes = Record<string, { item_id?: string }>;
type Roll = Record<string, unknown>;
const uuid = (value: string): boolean => /^[\da-f]{8}(?:-[\da-f]{4}){3}-[\da-f]{12}$/i.test(value);
const ticks = ['damage_over_time_roll', 'regeneration_roll', 'reduce_mana_roll', 'increase_mana_roll',
    'increase_luck_roll', 'temporary_vitality_regeneration_roll', 'reduce_luck_roll'];
const grants = ['temporary_vitality_roll', 'temporary_max_vitality_roll'];
/** Roll rows that can carry a resolved visual, shared by normalization and the local relay. */
export function isResolvedRollVfxType(type: unknown, types: RollVfxTypes): boolean {
    return type === 'no_roll' || ['damage_roll', 'healing_roll', ...grants, ...ticks]
        .some(name => !!types[name]?.item_id && types[name]!.item_id === type);
}
const numeric = (value: unknown): number | null =>
    (typeof value === 'number' || typeof value === 'string' && value.trim() !== '') && Number.isFinite(Number(value))
        ? Number(value) : null;

const record = (value: unknown): Roll => value && typeof value === 'object' && !Array.isArray(value) ? value as Roll : {};
const upper = (value: unknown): string => String(value ?? '').toUpperCase();

/** TG already holds published recipes in SELECT metadata before its definition assets are warm. */
export function resolveRollVfxRecipe(id: string, used: Roll | undefined, vfxRows: readonly Roll[] = []): unknown {
    return record(used?.assets).magic_vfx ?? inventoryDefinitionVfxRecipe(id, vfxRows);
}

/** Centered blast art belongs to the confirmed placement, not each damage recipient. */
export function abilityVfxUsesAreaCast(recipe: Roll, used: Roll | undefined): boolean {
    if (recipe.kind === 'area') return true;
    const type = layerOf(used, 'aoe_type');
    if (recipe.areaCastCue === true) return recipe.kind === 'contact' && recipe.anchor === 'caster' && type != null;
    if (recipe.kind !== 'burst' || typeof type !== 'string') return false;
    const shape = SHAPES[upper(type)] ?? type;
    return ['sphere', 'circle', 'cylinder', 'cube', 'square'].includes(shape.toLowerCase());
}

/** A transport address, not a gameplay identity. Repeated equal rows occupy ordered slots. */
export function rollPresentationKey(roll: Roll): string {
    return JSON.stringify([roll.roll_type_1, roll.roll_item_id, roll.roll_by, roll.roll_to].map(upper));
}

/** Capture finalized row-plan slots before enqueue/POST, retaining decisions already captured on a retry.
 * Late boundary ticks append only their missing slots. SQL may assign another roll id/serial.
 * Only presentation fields cross this contract; persisted actor/parent authority stays in the commit. */
export function captureCommandRollVfx(command: Roll, held: (id: string) => Roll | undefined, types: RollVfxTypes,
    vfxRows: readonly Roll[] = []): void {
    const actionId = upper(command.operation_id);
    if (!actionId) return;
    const verdict = record(command.verdict ?? command.after ?? command.frame ?? command);
    const snapshots = record(command.roll_presentations) as Record<string, Roll[]>;
    const offsets: Record<string, number> = {};
    let slot = Object.values(snapshots).reduce((count, rows) => count + rows.length, 0);
    const capture = (input: Roll): void => {
        const type = input.roll_type_1;
        if (typeof type !== 'string' || !type) return;
        const itemId = upper(input.roll_item_id ?? command.ability_id ?? command.weapon_id);
        const eligible = isResolvedRollVfxType(type, types) || type === 'spell_attack_missed';
        if (!eligible) return;
        const by = upper(input.roll_by ?? command.actor_id), to = upper(input.roll_to ?? command.target_id);
        const key = rollPresentationKey({ roll_type_1: type, roll_item_id: itemId, roll_by: by, roll_to: to });
        const offset = offsets[key] ?? 0;
        offsets[key] = offset + 1;
        if (snapshots[key]?.[offset]) return;
        const used = held(itemId);
        const recipe = resolveRollVfxRecipe(itemId, used, vfxRows);
        const roll = normalizeRollVfx({ ...input, roll_id: actionId, roll_i: ++slot,
            roll_item_id: itemId, roll_by: by, roll_to: to, encounter_id: upper(command.encounter_id),
            roll_item_id_item_type: used ? itemField(used, 'item_type') : '',
            roll_result: input.roll_result ?? input.result ?? 0,
            roll_feedback: input.roll_feedback ?? input.feedback ?? null,
            roll_cast_source_id: input.roll_cast_source_id !== undefined ? input.roll_cast_source_id
                : input.cast_source_id !== undefined ? input.cast_source_id : command.cast_source_id ?? null,
            roll_vfx_recipe: recipe, roll_vfx_area_cast: abilityVfxUsesAreaCast(record(recipe), used),
            roll_motion: input.roll_motion,
        }, types) as Roll;
        (snapshots[key] ??= []).push({ actionId, recipe,
            cue: roll.roll_vfx, clock: roll.roll_cast_clock ?? null,
            diagnostics: roll.roll_vfx_diagnostics ?? [], result: roll.roll_vfx_result });
    };
    if (command.schema === 'janus.action.attack.v2') localAttackRolls(command, verdict).forEach(capture);
    else {
        const visit = (value: unknown): void => {
            if (Array.isArray(value)) { value.forEach(visit); return; }
            const entry = record(value);
            if (typeof entry.roll_type_1 === 'string') { capture(entry); return; }
            // These are row-plan containers, never a walk of held encounter state.
            for (const key of ['rows', 'steps', 'verdict', 'after', 'frame', 'gm_boundary', 'tick']) if (entry[key]) visit(entry[key]);
        };
        visit(command);
    }
    if (Object.keys(snapshots).length) command.roll_presentations = snapshots;
    if (Array.isArray(verdict.areaCasts)) verdict.areaCasts.forEach((value, index) => {
        const cast = record(value);
        if (cast.cue) return;
        cast.actionId = actionId;
        cast.cue = { schema: 'ft.ft3d.magic-cue.v1', id: `${actionId}:${upper(cast.itemId)}:area:${index}`,
            itemId: upper(cast.itemId), encounterId: upper(command.encounter_id), sourceEntityId: upper(command.actor_id),
            targetEntityId: upper(command.actor_id), recipe: cast.recipe, area: cast.area };
    });
}

function resolvedCue(roll: Roll, recipe: MagicRecipe, matches: (names: string[]) => boolean,
    resolved: boolean, tick: boolean, positive: boolean, grantNoop: boolean): Roll | null {
    // These visuals belong to displacement, landed status, area placement or the summoned child.
    if (recipe.projectileStyle === 'purchased-wind-push'
        || ['mortality-mist', 'crimson-fog'].includes(recipe.bodyStyle ?? '')
        || recipe.contactStyle === 'purchased-dark-swipe'
            && String(roll.roll_item_id || '').toUpperCase() === 'B5AF1D1B-C91A-7619-653C-8CCB1031C5F9'
        || recipe.areaCastCue || recipe.summonArrival || recipe.lifecycle === 'status' || recipe.kind === 'area') return null;
    // A missed attack still releases its authored flight/swing; it applies no landed contact/status.
    if (!resolved && !(roll.roll_type_1 === 'spell_attack_missed'
        && ['projectile', 'beam', 'weapon'].includes(recipe.kind))) return null;
    if (tick && (recipe.kind !== 'contact' || recipe.anchor !== 'target' || !positive) || grantNoop) return null;
    if (tick) {
        const { castTiming: _castTiming, vfxDelayMs: _vfxDelayMs, ...contact } = recipe;
        recipe = contact;
    }
    if (recipe.beamStyle === 'crimson-transfer'
        && (!matches(['healing_roll']) || !positive || roll.roll_by === roll.roll_to)) return null;
    if ((recipe.beamStyle === 'chrono-lash'
        || ['purchased-acid-swipe', 'purchased-dark-swipe', 'purchased-dark-hit'].includes(recipe.contactStyle ?? ''))
        && !matches(['damage_roll'])) return null;
    if (recipe.branches) {
        const feedback = roll.roll_feedback as { damage_types?: unknown } | null;
        let texture: string | undefined, best = -1;
        for (const typed of Array.isArray(feedback?.damage_types) ? feedback.damage_types : []) {
            const amount = numeric(typed?.rolled);
            const branch = recipe.branches[String(typed?.damage_type_id || '').toUpperCase()];
            // Ties retain the first feedback entry. An unmapped type never invents a fallback.
            if (branch && amount !== null && amount > best) { texture = branch; best = amount; }
        }
        if (!texture) return null;
        const { branches: _branches, ...selected } = recipe;
        recipe = { ...selected, texture };
    }
    const itemId = String(roll.roll_item_id || '').toUpperCase();
    const source = String(roll.roll_by || '').toUpperCase();
    const target = String(roll.roll_to || '').toUpperCase();
    const action = String(roll.roll_id || '');
    const identity = recipe.kind === 'weapon' ? source
        : ['projectile', 'beam'].includes(recipe.kind) ? String(roll.roll_i ?? '') : target;
    if (!uuid(itemId) || !uuid(source) || recipe.anchor === 'target' && !uuid(target) || !action || !identity) return null;
    return { schema: 'ft.ft3d.magic-cue.v1', id: [action, itemId, source, identity].join(':'), itemId,
        encounterId: String(roll.encounter_id || ''), sourceEntityId: source, targetEntityId: target, recipe,
        ...(tick ? { sourceKind: 'effect' } : ['attack_ability', 'opportunity_attack_ability'].includes(String(roll.roll_item_id_item_type || '').toLowerCase())
            ? { sourceKind: 'attack' } : {}),
        ...(typeof roll.roll_motion === 'string' ? { motion: roll.roll_motion } : {}) };
}

/** Live-arrival presentation only. Mutate the canonical row once, before audio and authorized fan-out.
 * Saved recipes stay untouched; dedicated area/target cues and pre-cutover frames pass through intact.
 * History renderers never call this function. Cosmetic diagnostics cannot refuse committed gameplay. */
export function normalizeRollVfx<T extends Roll>(roll: T, types: RollVfxTypes): T {
    const presentation = record(roll.roll_presentation);
    if (Object.keys(presentation).length) {
        const out: Roll = roll;
        if (Object.prototype.hasOwnProperty.call(presentation, 'recipe')) out.roll_vfx_recipe = presentation.recipe;
        out.roll_vfx = presentation.cue ?? null;
        out.roll_vfx_result = presentation.result;
        if (presentation.clock) out.roll_cast_clock = presentation.clock;
        else delete out.roll_cast_clock;
        if (Array.isArray(presentation.diagnostics) && presentation.diagnostics.length) out.roll_vfx_diagnostics = presentation.diagnostics;
        else delete out.roll_vfx_diagnostics;
        return roll;
    }
    const saved = Object.prototype.hasOwnProperty.call(roll, 'roll_vfx_recipe');
    if (roll.roll_vfx || !saved && Object.prototype.hasOwnProperty.call(roll, 'roll_vfx')) return roll;
    const out: Roll = roll;
    out.roll_vfx = null;
    out.roll_vfx_result = 'absent-recipe';
    delete out.roll_cast_clock;
    delete out.roll_vfx_diagnostics;
    const matches = (names: string[]): boolean => names.some(name => !!types[name]?.item_id && types[name]!.item_id === roll.roll_type_1);
    const tick = matches(ticks);
    const castId = castCommitId(roll);
    const effect = !castId && (tick || isCastEffectRow(roll));
    const resolved = isResolvedRollVfxType(roll.roll_type_1, types);
    const positive = (numeric(roll.roll_result) ?? 0) > 0;
    const grantNoop = matches(grants) && (!positive || String(roll.roll_type_2 ?? '') !== '');
    const snapshot = roll.roll_vfx_recipe;
    // insert_rolls_inc.php stores vfx:null when the item has no visual binding.
    if (snapshot !== null && snapshot !== undefined) {
        const checked = validateVfxRecipe(snapshot, {});
        if (checked.kind === 'invalid') { out.roll_vfx_diagnostics = checked.diagnostics; out.roll_vfx_result = 'unsupported-recipe'; return roll; }
        if (checked.kind === 'intentional-none') { out.roll_vfx_result = 'intentional-none'; return roll; }
        const recipe = checked.value;
        const recipient = recipe.kind === 'area' ? recipe.recipientHit : recipe;
        if (recipient && !roll.roll_vfx_area_cast) out.roll_vfx = resolvedCue(roll, recipient, matches, resolved, effect, positive, grantNoop);
        out.roll_vfx_result = out.roll_vfx ? 'cue' : 'dedicated-or-suppressed';
        if (!out.roll_vfx && castId && resolved && !effect && !grantNoop && recipe.castTiming === 'gesture-release-v1'
            && (roll.roll_vfx_area_cast || recipe.kind === 'area' || recipe.areaCastCue)) {
            out.roll_cast_clock = { castTiming: recipe.castTiming,
                ...(recipe.vfxDelayMs !== undefined ? { vfxDelayMs: recipe.vfxDelayMs } : {}) };
        }
    } else if (castId && isCastItemRoll(roll) && resolved && !effect && !grantNoop && !roll.roll_motion) {
        out.roll_cast_clock = { castTiming: 'gesture-release-v1', gesture: 'cast' };
    }
    return roll;
}
